import { useEffect, useState } from "react";
import { CheckCircle2, PauseCircle, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const ROWS = [
  ["From name", (c) => c.from_name],
  ["From email", (c) => c.from_email],
  ["Reply-to", (c) => c.reply_to || "not set"],
  ["Provider configured", (c) => (c.provider_configured ? "Yes" : "No")],
];

export const EmailDeliveryCard = () => {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/email-delivery");
      setConfig(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load email delivery status");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (error) {
    return <p className="text-xs text-[#757675]" data-testid="email-delivery-error">{error}</p>;
  }
  if (!config) return null;

  const enabled = config.delivery_enabled;
  return (
    <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6" data-testid="email-delivery-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1A1B1A] flex items-center gap-2">
            Email delivery
            <span
              data-testid="email-delivery-status"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                enabled ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#1A1B1A]/[0.05] text-[#757675]"
              }`}
            >
              {enabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
              {config.status_label}
            </span>
          </h3>
          <p className="text-xs text-[#757675] mt-1" data-testid="email-delivery-explainer">
            {enabled
              ? "Your clinic's emails are being sent to patients and staff."
              : "Email sending is switched off for your clinic, so no messages are sent to patients. Your sender details below are saved and ready for when it is turned on."}
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="email-delivery-refresh-btn"
          className="flex items-center gap-1 text-xs text-[#205B5A] hover:underline disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <dl className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-xs" data-testid="email-delivery-rows">
        {ROWS.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 border-b border-[#1A1B1A]/[0.04] pb-1.5">
            <dt className="text-[#757675]">{label}</dt>
            <dd className="text-[#1A1B1A] text-right break-words">{value(config)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default EmailDeliveryCard;
